/**
 * WorkflowOrderPanel — expanded panel for a single QueueEntry.
 *
 * Layout (4 rows):
 *  1. Customer info (left) | Order totals (right) — side by side
 *  2. Items — fixed-height scrollable box, ≤4 visible, overflow indicator
 *  3. Action controls — outcome select + note + submit (session auto-opened server-side)
 *  4. Escalation (when workflow supports it) + Order History toggle
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  BlockStack, Button, Divider, InlineStack,
  Select, Text, TextField,
} from '@shopify/polaris'
import { XSmallIcon, DuplicateIcon } from '@shopify/polaris-icons'
import { engineApi } from '../../sdk/engine.api'
import type {
  QueueEntry, WorkflowSurface, WorkflowPermissions,
  CloseSessionBody, TimelineEvent, EscalationReason,
} from '../../sdk/engine.api'

type Props = {
  entry:        QueueEntry
  surface:      WorkflowSurface
  permissions:  WorkflowPermissions
  shop:         string
  onClose:      () => void
  onMutated:    () => void
  extraAction?: React.ReactNode
}

const LABEL: React.CSSProperties = { color: '#6d7175', fontSize: 13, minWidth: 90 }
const VAL:   React.CSSProperties = { color: '#202223', fontSize: 13 }

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <InlineStack gap="200" blockAlign="center">
      <span style={LABEL}>{label}</span>
      <span style={VAL}>{value}</span>
    </InlineStack>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}

function formatAt(iso: string): string {
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function formatShort(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch { return iso }
}

// ── Timeline ─────────────────────────────────────────────────────────────────

function TimelineSection({
  shop, shopifyOrderId, lastStatus, lastDate,
}: {
  shop: string; shopifyOrderId: string
  lastStatus: string | undefined; lastDate: string | undefined
}) {
  const [open,    setOpen]    = useState(false)
  const [events,  setEvents]  = useState<TimelineEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded,  setLoaded]  = useState(false)

  const load = useCallback(async () => {
    if (loaded) return
    setLoading(true)
    try {
      const res = await engineApi.timeline(shop, shopifyOrderId)
      setEvents(res.events)
      setLoaded(true)
    } catch {
      setLoaded(true)
    } finally {
      setLoading(false)
    }
  }, [shop, shopifyOrderId, loaded])

  const toggle = () => {
    if (!open) load()
    setOpen(o => !o)
  }

  const kindLabel: Record<string, string> = {
    entry:         'Added to queue',
    session_open:  'Session opened',
    session_close: 'Session closed',
    escalation:    'Escalated',
  }

  const inlineHint = lastStatus
    ? ` [${lastStatus}${lastDate ? ' · ' + lastDate : ''}]`
    : ''

  return (
    <div>
      <button
        onClick={toggle}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, color: '#2c6ecb', padding: 0, fontWeight: 600,
        }}
      >
        {open ? '▾' : '▸'} Order History
        {!open && <span style={{ fontWeight: 400, color: '#6d7175' }}>{inlineHint}</span>}
      </button>

      {open && (
        <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: '2px solid #e1e3e5' }}>
          {loading && <Text as="p" variant="bodySm" tone="subdued">Loading…</Text>}
          {!loading && events.length === 0 && (
            <Text as="p" variant="bodySm" tone="subdued">No history yet.</Text>
          )}
          {events.map((ev, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <InlineStack gap="200" blockAlign="start">
                <span style={{ fontSize: 12, color: '#6d7175', minWidth: 130 }}>
                  {formatAt(ev.at)}
                </span>
                <BlockStack gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">
                    {kindLabel[ev.kind] ?? ev.kind.replace(/_/g, ' ')}
                    {ev.workflowId && (
                      <span style={{ fontWeight: 400, color: '#6d7175', marginLeft: 6 }}>
                        [{ev.workflowId.replace(/_/g, ' ')}]
                      </span>
                    )}
                  </Text>
                  {ev.detail?.['outcomeId'] != null && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      Outcome: {String(ev.detail['outcomeId'] as string)}
                    </Text>
                  )}
                  {ev.detail?.['note'] != null && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      Note: {String(ev.detail['note'] as string)}
                    </Text>
                  )}
                </BlockStack>
              </InlineStack>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Delivery Timeline ─────────────────────────────────────────────────────────

type DeliveryTimelineData = {
  timeline: { value: string; code: number; done: boolean; date: string; desc?: string }[]
  attempts: { type: string; attemptDate: string; succeededAt: string | null; courier: string | null }[]
  deliveryAttemptsLength: number
}

const DELIVERY_LABEL: Record<string, string> = {
  new:                  'Created',
  picked_up:            'Picked Up',
  in_transit:           'In Transit',
  out_for_delivery:     'Out for Delivery',
  delivered:            'Delivered',
  out_for_return:       'Out for Return',
  returned_to_business: 'Returned',
}
const DELIVERY_COLOR: Record<string, string> = {
  new:                  '#5c5f62',
  picked_up:            '#2c6ecb',
  in_transit:           '#2c6ecb',
  out_for_delivery:     '#b98900',
  delivered:            '#108043',
  out_for_return:       '#d82c0d',
  returned_to_business: '#d82c0d',
}

function DeliveryTimelineSection({ shop, trackingNumber }: { shop: string; trackingNumber: string }) {
  const [open,    setOpen]    = useState(false)
  const [data,    setData]    = useState<DeliveryTimelineData | null>(null)
  const [loading, setLoading] = useState(false)
  const [loaded,  setLoaded]  = useState(false)

  const load = useCallback(async () => {
    if (loaded) return
    setLoading(true)
    try {
      const res = await engineApi.deliveryTimeline(shop, trackingNumber)
      setData(res)
    } catch { /* silently ignore */ }
    finally { setLoaded(true); setLoading(false) }
  }, [shop, trackingNumber, loaded])

  const toggle = () => {
    if (!open) load()
    setOpen(o => !o)
  }

  const doneSteps = data?.timeline.filter(s => s.done).length ?? 0
  const totalSteps = data?.timeline.length ?? 0

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={toggle}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#2c6ecb', padding: 0, fontWeight: 600 }}
      >
        {open ? '▾' : '▸'} Delivery Timeline
        {!open && data && (
          <span style={{ fontWeight: 400, color: '#6d7175', marginLeft: 6 }}>
            [{doneSteps}/{totalSteps} steps
            {data.deliveryAttemptsLength > 0 && ` · ${data.deliveryAttemptsLength} attempt${data.deliveryAttemptsLength > 1 ? 's' : ''}`}]
          </span>
        )}
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {loading && <Text as="p" variant="bodySm" tone="subdued">Loading…</Text>}
          {!loading && loaded && !data && (
            <Text as="p" variant="bodySm" tone="subdued">Could not load delivery status.</Text>
          )}
          {!loading && data && data.timeline.length > 0 && (
            <div style={{ position: 'relative', paddingLeft: 22 }}>
              {/* Vertical line */}
              <div style={{
                position: 'absolute', left: 5, top: 6, bottom: 6,
                width: 2, background: '#e1e3e5',
              }} />
              {data.timeline.map((step, i) => {
                const color = step.done ? (DELIVERY_COLOR[step.value] ?? '#2c6ecb') : '#c9cccf'
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, position: 'relative' }}>
                    {/* Dot */}
                    <div style={{
                      position: 'absolute', left: -22, top: 3,
                      width: 12, height: 12, borderRadius: '50%',
                      background: step.done ? color : '#fff',
                      border: `2px solid ${color}`,
                      boxSizing: 'border-box', zIndex: 1,
                    }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: step.done ? 600 : 400, color: step.done ? (DELIVERY_COLOR[step.value] ?? '#202223') : '#8c9196' }}>
                        {DELIVERY_LABEL[step.value] ?? step.value.replace(/_/g, ' ')}
                        {step.desc && (
                          <span style={{ fontWeight: 400, color: '#6d7175', marginLeft: 6, fontSize: 12 }}>
                            ({step.desc})
                          </span>
                        )}
                      </div>
                      {step.done && step.date && (
                        <div style={{ fontSize: 12, color: '#6d7175', marginTop: 1 }}>
                          {formatAt(step.date)}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {/* Attempts detail */}
          {!loading && data && data.attempts.length > 0 && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: '#f6f6f7', borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6d7175', marginBottom: 4 }}>
                Delivery Attempts ({data.attempts.length})
              </div>
              {data.attempts.map((a, i) => (
                <div key={i} style={{ fontSize: 12, color: '#202223', marginBottom: 3 }}>
                  <span style={{ color: '#6d7175' }}>#{i + 1}</span>
                  {' · '}
                  {formatAt(a.attemptDate)}
                  {a.succeededAt && <span style={{ color: '#108043', marginLeft: 6 }}>✓ Delivered {formatAt(a.succeededAt)}</span>}
                  {a.courier && <span style={{ color: '#6d7175', marginLeft: 6 }}>— {a.courier}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Customer History ─────────────────────────────────────────────────────────

type RecentOrder = { name: string; date: string; financialStatus: string; total: string }

function statusBg(fs: string): { background: string; borderLeft: string } {
  const s = fs.toLowerCase()
  if (s === 'voided' || s === 'refunded')
    return { background: '#fff4f4', borderLeft: '3px solid #d82c0d' }
  if (s === 'paid')
    return { background: '#f0fff4', borderLeft: '3px solid #108043' }
  return { background: '#fffbe6', borderLeft: '3px solid #ffc453' }
}

function CustomerHistorySection({
  orders, currentOrderName,
}: {
  orders: RecentOrder[]; currentOrderName: string
}) {
  const [open, setOpen] = useState(false)
  const visible = orders.filter(o => o.name !== currentOrderName)
  if (visible.length === 0) return null

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#2c6ecb', padding: 0, fontWeight: 600 }}
      >
        {open ? '▾' : '▸'} Customer History
        <span style={{ marginLeft: 6, display: 'inline-flex', gap: 3, verticalAlign: 'middle' }}>
          {visible.map((o, i) => {
            const s = o.financialStatus.toLowerCase()
            const dot = s === 'voided' || s === 'refunded' ? '#d82c0d'
                      : s === 'paid'                       ? '#108043'
                      :                                      '#ffc453'
            return (
              <span
                key={i}
                title={`${o.name} — ${o.financialStatus}`}
                style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dot }}
              />
            )
          })}
        </span>
        {!open && (
          <span style={{ fontWeight: 400, color: '#6d7175', marginLeft: 4 }}>
            [{visible.length} order{visible.length > 1 ? 's' : ''}]
          </span>
        )}
      </button>

      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visible.map((o, i) => {
            const bg = statusBg(o.financialStatus)
            return (
              <div
                key={i}
                style={{
                  ...bg,
                  borderRadius: 4,
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 500, color: '#202223' }}>{o.name}</span>
                <InlineStack gap="200">
                  {o.total && <span style={{ color: '#6d7175' }}>{o.total} EGP</span>}
                  <span style={{ color: '#6d7175', textTransform: 'capitalize' }}>
                    {o.financialStatus.replace(/_/g, ' ')}
                  </span>
                  {o.date && <span style={{ color: '#8c9196' }}>{formatDate(o.date)}</span>}
                </InlineStack>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Escalation ────────────────────────────────────────────────────────────────

function EscalationSection({
  shop, surface, entry, onEscalated,
}: {
  shop: string; surface: WorkflowSurface; entry: QueueEntry; onEscalated: () => void
}) {
  const reasons: EscalationReason[] = surface.escalationReasons
  const [open,    setOpen]    = useState(false)
  const [reasonId, setReasonId] = useState(reasons[0]?.id ?? '')
  const [note,    setNote]    = useState('')
  const [loading, setLoading] = useState(false)
  const [err,     setErr]     = useState<string | null>(null)

  if (reasons.length === 0) return null

  const handleSubmit = async () => {
    setLoading(true)
    setErr(null)
    try {
      await engineApi.createEscalation(shop, {
        originWorkflow:     surface.definition.id,
        originQueueEntryId: entry.id,
        reasonId,
        note: note || undefined,
      })
      onEscalated()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Escalation failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 13, color: '#d97706', padding: 0, fontWeight: 600,
        }}
      >
        {open ? '▾' : '▸'} Escalate
      </button>

      {open && (
        <div style={{ marginTop: 8, padding: '12px', background: '#fffbf0', borderRadius: 6, border: '1px solid #fce4b3' }}>
          <BlockStack gap="200">
            <Select
              label="Escalation Reason"
              options={reasons.map(r => ({ label: r.name, value: r.id }))}
              value={reasonId}
              onChange={setReasonId}
            />
            <TextField
              label="Note (optional)"
              value={note}
              onChange={setNote}
              multiline={2}
              autoComplete="off"
            />
            <Button
              variant="primary" tone="critical"
              loading={loading}
              onClick={handleSubmit}
              disabled={!reasonId}
            >
              Submit Escalation
            </Button>
            {err && <Text as="p" variant="bodySm" tone="critical">{err}</Text>}
          </BlockStack>
        </div>
      )}
    </div>
  )
}

// ── Order Creation ───────────────────────────────────────────────────────────

type FulfillableLine = {
  id: string; sku: string; title: string; quantity: number; price: string
  fulfillmentLineItemId: string | null
}

type SkuItem = { sku: string; quantity: number; price: string }

function SkuItemsEditor({
  items,
  onChange,
}: {
  items: SkuItem[]
  onChange: (items: SkuItem[]) => void
}) {
  function update(idx: number, field: keyof SkuItem, val: string) {
    onChange(items.map((it, i) => i === idx
      ? { ...it, [field]: field === 'quantity' ? (parseInt(val, 10) || 1) : val }
      : it,
    ))
  }
  return (
    <BlockStack gap="150">
      {items.map((it, idx) => (
        <InlineStack key={idx} gap="200" blockAlign="center">
          <div style={{ flex: 3 }}>
            <TextField
              label={idx === 0 ? 'SKU' : ''}
              labelHidden={idx > 0}
              value={it.sku}
              onChange={v => update(idx, 'sku', v)}
              placeholder="Enter SKU"
              autoComplete="off"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              label={idx === 0 ? 'Qty' : ''}
              labelHidden={idx > 0}
              type="number"
              value={String(it.quantity)}
              onChange={v => update(idx, 'quantity', v)}
              min={1}
              autoComplete="off"
            />
          </div>
          <div style={{ flex: 2 }}>
            <TextField
              label={idx === 0 ? 'Price (EGP)' : ''}
              labelHidden={idx > 0}
              type="number"
              value={it.price}
              onChange={v => update(idx, 'price', v)}
              min={0}
              placeholder="0"
              autoComplete="off"
            />
          </div>
          {items.length > 1 && (
            <button
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d82c0d', fontSize: 16, marginTop: idx === 0 ? 20 : 0 }}
            >×</button>
          )}
        </InlineStack>
      ))}
      <Button size="slim" variant="plain" onClick={() => onChange([...items, { sku: '', quantity: 1, price: '' }])}>
        + Add item
      </Button>
    </BlockStack>
  )
}

function ReturnLineSelector({
  lines,
  selected,
  onChange,
}: {
  lines: FulfillableLine[]
  selected: Record<string, { checked: boolean; qty: number }>
  onChange: (sel: Record<string, { checked: boolean; qty: number }>) => void
}) {
  const fulfillable = lines.filter(li => li.fulfillmentLineItemId)
  if (fulfillable.length === 0) {
    return <Text as="p" variant="bodySm" tone="critical">No fulfillable items — order may not be shipped yet.</Text>
  }
  return (
    <BlockStack gap="100">
      {fulfillable.map(li => {
        const key  = li.fulfillmentLineItemId!
        const row  = selected[key] ?? { checked: true, qty: li.quantity }
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={row.checked}
              onChange={e => onChange({ ...selected, [key]: { ...row, checked: e.target.checked } })}
              style={{ cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={{ fontSize: 13, flex: 1, color: row.checked ? '#202223' : '#8c9196', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {li.title}
              {li.sku && <span style={{ marginLeft: 6, fontSize: 12, color: '#6d7175', fontFamily: 'monospace' }}>{li.sku}</span>}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button
                disabled={!row.checked}
                onClick={() => onChange({ ...selected, [key]: { ...row, qty: Math.max(1, row.qty - 1) } })}
                style={{ width: 20, height: 20, border: '1px solid #c9cccf', borderRadius: 3, background: '#f6f6f7', cursor: 'pointer', fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >−</button>
              <span style={{ fontSize: 13, minWidth: 18, textAlign: 'center', color: row.checked ? '#202223' : '#8c9196' }}>{row.qty}</span>
              <button
                disabled={!row.checked || row.qty >= li.quantity}
                onClick={() => onChange({ ...selected, [key]: { ...row, qty: Math.min(li.quantity, row.qty + 1) } })}
                style={{ width: 20, height: 20, border: '1px solid #c9cccf', borderRadius: 3, background: '#f6f6f7', cursor: 'pointer', fontSize: 13, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >+</button>
            </div>
          </div>
        )
      })}
    </BlockStack>
  )
}

function OrderCreationSection({
  shop, workflowId, entryId, shopifyOrderId, customer, defaultLineItems,
  canCreate, canExchange, canReturn,
}: {
  shop: string; workflowId: string; entryId: string; shopifyOrderId: string
  customer: { name: string; phone: string; city: string; address: string } | null
  defaultLineItems: FulfillableLine[]
  canCreate: boolean; canExchange: boolean; canReturn: boolean
}) {
  const tabs = useMemo(() => [
    ...(canCreate   ? ['new']      as const : []),
    ...(canExchange ? ['exchange'] as const : []),
    ...(canReturn   ? ['return']   as const : []),
  ], [canCreate, canExchange, canReturn])

  const [open,    setOpen]    = useState(false)
  const [tab,     setTab]     = useState<'new' | 'exchange' | 'return'>(tabs[0] ?? 'new')

  // 'new' tab state
  const [newItems,  setNewItems]  = useState<SkuItem[]>([{ sku: '', quantity: 1, price: '' }])
  const [priority,  setPriority]  = useState(false)

  // exchange/return — which lines to send back
  const initReturnSel = useMemo(() => {
    const init: Record<string, { checked: boolean; qty: number }> = {}
    for (const li of defaultLineItems) {
      if (li.fulfillmentLineItemId) {
        init[li.fulfillmentLineItemId] = { checked: true, qty: li.quantity }
      }
    }
    return init
  }, [defaultLineItems])
  const [returnSel, setReturnSel] = useState<Record<string, { checked: boolean; qty: number }>>(initReturnSel)

  // 'exchange' tab — new items to send out
  const [exchItems, setExchItems] = useState<SkuItem[]>([{ sku: '', quantity: 1, price: '' }])

  // shared
  const [note,   setNote]   = useState('')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [err,    setErr]    = useState<string | null>(null)

  // Re-init returnSel when defaultLineItems change (new order loaded)
  useEffect(() => { setReturnSel(initReturnSel) }, [initReturnSel])

  if (tabs.length === 0) return null

  const tabLabel: Record<string, string> = { new: 'New', exchange: 'Exchange', return: 'Return' }

  function buildReturnItems(sel: Record<string, { checked: boolean; qty: number }>) {
    return defaultLineItems
      .filter(li => li.fulfillmentLineItemId && sel[li.fulfillmentLineItemId]?.checked)
      .map(li => ({
        lineItemId:            li.id,
        fulfillmentLineItemId: li.fulfillmentLineItemId!,
        quantity:              sel[li.fulfillmentLineItemId!]?.qty ?? li.quantity,
        unitPrice:             parseFloat(li.price) || 0,
        sku:                   li.sku || undefined,
      }))
  }

  async function handleSubmit() {
    if (!customer) { setErr('No customer data available'); return }
    setSaving(true); setErr(null); setResult(null)
    try {
      if (tab === 'new') {
        const items = newItems.filter(it => it.sku.trim())
        if (items.length === 0) { setErr('Add at least one SKU'); setSaving(false); return }
        const res = await engineApi.createOrder(shop, workflowId, entryId, {
          type: 'new', customer,
          newItems: items.map(it => ({ sku: it.sku, quantity: it.quantity })),
          priority: priority || undefined,
          note: note || undefined,
        })
        setResult(`New order created: ${res.type === 'new' ? res.orderName : ''}`)
        setNewItems([{ sku: '', quantity: 1, price: '' }]); setPriority(false)

      } else if (tab === 'exchange') {
        const returnItems  = buildReturnItems(returnSel)
        const exchangeItems = exchItems
          .filter(it => it.sku.trim())
          .map(it => ({ sku: it.sku, quantity: it.quantity, unitPrice: parseFloat(it.price) || 0 }))
        if (returnItems.length === 0)  { setErr('Select at least one item to return'); setSaving(false); return }
        if (exchangeItems.length === 0) { setErr('Add at least one exchange item'); setSaving(false); return }
        const res = await engineApi.createOrder(shop, workflowId, entryId, {
          type: 'exchange', shopifyOrderId, customer,
          returnItems, exchangeItems, note: note || undefined,
        })
        if (res.type === 'exchange') {
          setResult(`Exchange registered: ${res.returnName} — awaiting FIFO allocation`)
        }
        setExchItems([{ sku: '', quantity: 1, price: '' }])

      } else {
        const returnLineItems = buildReturnItems(returnSel)
        if (returnLineItems.length === 0) { setErr('Select at least one item to return'); setSaving(false); return }
        const res = await engineApi.createOrder(shop, workflowId, entryId, {
          type: 'return', shopifyOrderId, customer,
          returnLineItems, note: note || undefined,
        })
        if (res.type === 'return') {
          setResult(`Return shipment created: ${res.trackingNumber}`)
        }
      }
      setNote('')
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Order creation failed')
    } finally {
      setSaving(false)
    }
  }

  const submitLabel = tab === 'new' ? 'Create Order' : tab === 'exchange' ? 'Create Exchange Shipment' : 'Create Return Shipment'

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#008060', padding: 0, fontWeight: 600 }}
      >
        {open ? '▾' : '▸'} Create Order
      </button>

      {open && (
        <div style={{ marginTop: 8, padding: 12, background: '#f6fef9', borderRadius: 6, border: '1px solid #b5e4c8' }}>
          <BlockStack gap="200">

            {/* Tab switcher */}
            {tabs.length > 1 && (
              <InlineStack gap="200">
                {tabs.map(t => (
                  <button
                    key={t}
                    onClick={() => { setTab(t); setErr(null); setResult(null) }}
                    style={{
                      background: tab === t ? '#008060' : 'transparent',
                      color:      tab === t ? '#fff' : '#008060',
                      border: '1px solid #008060', borderRadius: 4,
                      padding: '3px 12px', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >{tabLabel[t]}</button>
                ))}
              </InlineStack>
            )}

            {tab === 'new' && (
              <BlockStack gap="150">
                <Text as="p" variant="bodySm" tone="subdued">
                  A new Shopify order will be created and routed to the Confirmation queue.
                </Text>
                <SkuItemsEditor items={newItems} onChange={setNewItems} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={priority} onChange={e => setPriority(e.target.checked)} />
                  Mark as priority
                </label>
              </BlockStack>
            )}

            {tab === 'exchange' && (
              <BlockStack gap="200">
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6d7175', marginBottom: 6 }}>Items to return</div>
                  <ReturnLineSelector lines={defaultLineItems} selected={returnSel} onChange={setReturnSel} />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6d7175', marginBottom: 6 }}>Items to send (exchange)</div>
                  <SkuItemsEditor items={exchItems} onChange={setExchItems} />
                </div>
              </BlockStack>
            )}

            {tab === 'return' && (
              <BlockStack gap="150">
                <Text as="p" variant="bodySm" tone="subdued">
                  A Bosta return shipment (type 25) will be created to collect the selected items.
                </Text>
                <ReturnLineSelector lines={defaultLineItems} selected={returnSel} onChange={setReturnSel} />
              </BlockStack>
            )}

            <TextField
              label="Note (optional)"
              value={note}
              onChange={setNote}
              multiline={2}
              autoComplete="off"
            />

            <Button variant="primary" loading={saving} onClick={handleSubmit}>
              {submitLabel}
            </Button>

            {result && <Text as="p" variant="bodySm" tone="success">{result}</Text>}
            {err    && <Text as="p" variant="bodySm" tone="critical">{err}</Text>}
          </BlockStack>
        </div>
      )}
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function WorkflowOrderPanel({ entry, surface, permissions, shop, onClose, onMutated, extraAction }: Props) {
  const { definition, outcomes, statusSet } = surface
  const granted    = new Set(permissions.granted)
  const canWork    = permissions.bootstrap || granted.has('session.work')
  const canEscalate = permissions.bootstrap || granted.has('escalation.create')

  const isTerminal = entry.phase === 'resolved' || entry.phase === 'archived'

  // Order record
  const [record,    setRecord]   = useState<Record<string, string> | null>(null)
  const [customer,  setCustomer] = useState<{
    name: string; phone: string; city: string; address: string
    addressFields: { firstName: string; lastName: string; phone: string; province: string; city: string; address1: string; address2: string }
  } | null>(null)
  const [lineItems, setLineItems] = useState<{ id: string; sku: string; title: string; quantity: number; price: string; fulfillmentLineItemId: string | null }[]>([])
  const [summary,   setSummary]  = useState<{ subtotal: string; shipping: string; discount: string; discountCodes: string[]; total: string; paid: string; balance: string } | null>(null)
  const [recLoading, setRecL]   = useState(true)

  // Address edit
  const [editingAddr, setEditingAddr] = useState(false)
  const [addrForm, setAddrForm] = useState({ province: '', city: '', address1: '', address2: '' })
  const [savingAddr, setSavingAddr] = useState(false)
  const [addrError,  setAddrError]  = useState<string | null>(null)

  // Items draft edit
  const [itemsEditMode, setItemsEditMode] = useState(false)
  const [draftQty,      setDraftQty]      = useState<Record<string, number>>({})
  const [savingItems,   setSavingItems]   = useState(false)
  const [itemsError,    setItemsError]    = useState<string | null>(null)
  const [health,    setHealth]  = useState<{
    orderCount: number; cancellationRate: number; codRisk: number
    recentOrders: { name: string; date: string; financialStatus: string; total: string }[]
  } | null>(null)
  const [copied,    setCopied]  = useState(false)

  // Session — auto-opened server-side at distribution; we fetch/create on mount
  const [sessionId,      setSessionId]     = useState<string | null>(null)
  const [closingSession, setClosingSession] = useState(false)
  const [outcomeId,      setOutcomeId]     = useState(outcomes[0]?.id ?? '')
  const [note,           setNote]          = useState('')
  const [actionError,    setActionError]   = useState<string | null>(null)
  const [sessionError,   setSessionError]  = useState(false)

  const canOrderCreate   = permissions.bootstrap || granted.has('order.create')
  const canOrderExchange = permissions.bootstrap || granted.has('order.exchange')
  const canOrderReturn   = permissions.bootstrap || granted.has('order.return')
  const canOrderEdit     = permissions.bootstrap || granted.has('order.edit')

  useEffect(() => {
    setRecL(true)
    setHealth(null)
    setRecord(null)
    setCustomer(null)
    setLineItems([])
    setSummary(null)
    setSessionError(false)

    engineApi.orderRecord(shop, entry.orderRef.shopifyOrderId)
      .then(r => {
        setRecord(r.fields)
        setCustomer(r.customer)
        setLineItems(r.lineItems)
        setSummary(r.summary ?? null)
        if (r.customer?.phone) {
          engineApi.customerHealth(shop, r.customer.phone)
            .then(h => setHealth(h))
            .catch(() => {})
        }
      })
      .catch(() => setRecord(null))
      .finally(() => setRecL(false))

    // Fetch or create the session (server already opened it at distribution time)
    if (!isTerminal && canWork) {
      engineApi.openSession(shop, definition.id, entry.id)
        .then(({ session }) => setSessionId(session.id))
        .catch(() => setSessionError(true))
    }
  }, [shop, definition.id, entry.id, entry.orderRef.shopifyOrderId, isTerminal, canWork])

  const handleCloseSession = useCallback(async () => {
    if (!sessionId || !outcomeId) return
    setClosingSession(true)
    setActionError(null)
    try {
      const body: CloseSessionBody = { outcomeId, note: note || undefined }
      await engineApi.closeSession(shop, definition.id, sessionId, body)
      setSessionId(null)
      setNote('')
      onMutated()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to close session')
    } finally {
      setClosingSession(false)
    }
  }, [shop, definition.id, sessionId, outcomeId, note, onMutated])

  function copyPhone() {
    const phone = customer?.phone
    if (!phone) return
    navigator.clipboard.writeText(phone).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }

  const statusDef = statusSet.statuses.find(s => s.id === entry.status)

  const outcomeOptions = outcomes.map(o => ({ label: o.name, value: o.id }))

  // Last status/date for history toggle hint
  const lastStatusLabel = entry.customStatusName ?? statusDef?.name
  const lastDate        = entry.updatedAt ? formatShort(entry.updatedAt) : undefined

  const MAX_VISIBLE = 4

  return (
    <div style={{ padding: '4px 0' }}>

      {/* Close button — absolute top-right */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
        <InlineStack gap="200" blockAlign="center">
          {extraAction}
          <Button icon={XSmallIcon} variant="plain" onClick={onClose} accessibilityLabel="Collapse" />
        </InlineStack>
      </div>

      {/* ── Row 1: Customer | Order ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>

        {/* Customer */}
        <BlockStack gap="150">
          <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">Customer</Text>
          {recLoading
            ? <Text as="p" variant="bodySm" tone="subdued">Loading…</Text>
            : (
              <BlockStack gap="100">
                <FieldRow label="Name" value={customer?.name ?? entry.meta['customer_name'] as string | undefined} />

                {/* Phone + copy icon */}
                {customer?.phone && (
                  <InlineStack gap="100" blockAlign="center">
                    <span style={LABEL}>Phone</span>
                    <span style={VAL}>{customer.phone}</span>
                    <button
                      onClick={copyPhone}
                      title={copied ? 'Copied!' : 'Copy phone'}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: '0 2px', display: 'flex', alignItems: 'center',
                        color: copied ? '#008060' : '#6d7175',
                      }}
                    >
                      <DuplicateIcon width={14} height={14} />
                    </button>
                  </InlineStack>
                )}

                <FieldRow label="City" value={customer?.city} />
                {/* Address display or edit form */}
                {editingAddr && customer ? (
                  <div style={{ marginTop: 4 }}>
                    {addrError && <div style={{ fontSize: 12, color: '#d82c0d', marginBottom: 4 }}>{addrError}</div>}
                    {([
                      { key: 'province', label: 'Governorate' },
                      { key: 'city',     label: 'Area / City' },
                      { key: 'address1', label: 'Street' },
                      { key: 'address2', label: 'Details' },
                    ] as const).map(({ key, label }) => (
                      <div key={key} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 11, color: '#6d7175', marginBottom: 2 }}>{label}</div>
                        <input
                          value={addrForm[key]}
                          onChange={e => setAddrForm(f => ({ ...f, [key]: e.target.value }))}
                          style={{
                            width: '100%', boxSizing: 'border-box',
                            border: '1px solid #c9cccf', borderRadius: 4,
                            padding: '4px 8px', fontSize: 13,
                          }}
                        />
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <button
                        disabled={savingAddr}
                        onClick={async () => {
                          setSavingAddr(true); setAddrError(null)
                          try {
                            await engineApi.updateOrderAddress(shop, entry.orderRef.shopifyOrderId, {
                              ...customer.addressFields,
                              province: addrForm.province,
                              city:     addrForm.city,
                              address1: addrForm.address1,
                              address2: addrForm.address2,
                            })
                            setCustomer(c => c ? {
                              ...c,
                              city:    addrForm.province,
                              address: [addrForm.city, addrForm.address1, addrForm.address2].filter(Boolean).join(' / '),
                              addressFields: { ...c.addressFields, ...addrForm },
                            } : c)
                            setEditingAddr(false)
                          } catch (e) {
                            setAddrError(e instanceof Error ? e.message : 'Failed to update address')
                          } finally {
                            setSavingAddr(false)
                          }
                        }}
                        style={{
                          background: '#008060', color: '#fff', border: 'none',
                          borderRadius: 4, padding: '4px 12px', fontSize: 12,
                          cursor: savingAddr ? 'not-allowed' : 'pointer', opacity: savingAddr ? 0.7 : 1,
                        }}
                      >{savingAddr ? 'Saving…' : 'Save'}</button>
                      <button
                        onClick={() => { setEditingAddr(false); setAddrError(null) }}
                        style={{
                          background: 'none', border: '1px solid #c9cccf',
                          borderRadius: 4, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
                        }}
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={LABEL}>Address</span>
                    <span style={{ ...VAL, flex: 1 }}>{customer?.address}</span>
                    {!isTerminal && customer && (
                      <button
                        onClick={() => {
                          setAddrForm({
                            province: customer.addressFields.province,
                            city:     customer.addressFields.city,
                            address1: customer.addressFields.address1,
                            address2: customer.addressFields.address2,
                          })
                          setAddrError(null)
                          setEditingAddr(true)
                        }}
                        title="Edit address"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '0 2px', color: '#6d7175', fontSize: 11, flexShrink: 0,
                        }}
                      >Edit</button>
                    )}
                  </div>
                )}

                {/* Health */}
                {health && health.orderCount > 0 && (
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ ...LABEL }}>Health</span>
                    <InlineStack gap="150" wrap={false}>
                      <span style={{ fontSize: 12, color: '#2c6ecb' }}>{health.orderCount} orders</span>
                      {health.cancellationRate > 0 && (
                        <span style={{ fontSize: 12, color: health.cancellationRate > 30 ? '#d82c0d' : '#b98900' }}>
                          {health.cancellationRate}% cancelled
                        </span>
                      )}
                      {health.codRisk > 0 && (
                        <span style={{ fontSize: 12, color: '#5c5f62' }}>
                          {health.codRisk}% COD
                        </span>
                      )}
                    </InlineStack>
                  </InlineStack>
                )}
              </BlockStack>
            )
          }
        </BlockStack>

        {/* Order */}
        <BlockStack gap="150">
          <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">Order</Text>
          {recLoading
            ? <Text as="p" variant="bodySm" tone="subdued">Loading…</Text>
            : (
              <BlockStack gap="100">
                {summary ? (
                  <>
                    <FieldRow label={`Subtotal (${lineItems.length})`} value={`${parseFloat(summary.subtotal).toLocaleString('en-EG', { maximumFractionDigits: 2 })} EGP`} />
                    {parseFloat(summary.discount) > 0 && (
                      <InlineStack gap="200" blockAlign="center">
                        <span style={LABEL}>Discount</span>
                        <span style={{ color: '#108043', fontSize: 13 }}>
                          −{parseFloat(summary.discount).toLocaleString('en-EG', { maximumFractionDigits: 2 })} EGP
                          {summary.discountCodes.length > 0 && (
                            <span style={{ marginLeft: 6, color: '#6d7175', fontSize: 12 }}>
                              ({summary.discountCodes.join(', ')})
                            </span>
                          )}
                        </span>
                      </InlineStack>
                    )}
                    <FieldRow label="Shipping" value={`${parseFloat(summary.shipping).toLocaleString('en-EG', { maximumFractionDigits: 2 })} EGP`} />
                    <FieldRow label="Total"    value={`${parseFloat(summary.total).toLocaleString('en-EG', { maximumFractionDigits: 2 })} EGP`} />
                    <FieldRow label="Paid"     value={`${parseFloat(summary.paid).toLocaleString('en-EG', { maximumFractionDigits: 2 })} EGP`} />
                    {parseFloat(summary.balance) > 0 && (
                      <InlineStack gap="200" blockAlign="center">
                        <span style={{ ...LABEL, color: '#d82c0d' }}>Balance</span>
                        <span style={{ color: '#d82c0d', fontSize: 13, fontWeight: 600 }}>
                          {parseFloat(summary.balance).toLocaleString('en-EG', { maximumFractionDigits: 2 })} EGP
                        </span>
                      </InlineStack>
                    )}
                  </>
                ) : (
                  <FieldRow label="Total" value={record?.['total_price'] ? `${record['total_price']} EGP` : (entry.meta['total_price'] as string | undefined)} />
                )}
              </BlockStack>
            )
          }
        </BlockStack>
      </div>

      {/* ── Row 2: Items ── */}
      {!recLoading && lineItems.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <Text as="p" variant="bodySm" fontWeight="semibold" tone="subdued">Items</Text>
            {!isTerminal && !itemsEditMode && canOrderEdit && (
              <button
                onClick={() => {
                  const init: Record<string, number> = {}
                  lineItems.forEach(li => { init[li.id] = li.quantity })
                  setDraftQty(init)
                  setItemsError(null)
                  setItemsEditMode(true)
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#6d7175' }}
              >Edit</button>
            )}
          </div>

          <div style={{ border: `1px solid ${itemsEditMode ? '#2c6ecb' : '#e1e3e5'}`, borderRadius: 6, overflow: 'hidden', transition: 'border-color 0.15s' }}>
            {/* Column headers */}
            <div style={{
              display: 'grid', gridTemplateColumns: itemsEditMode ? '1fr 110px 96px 88px' : '1fr 110px 44px 88px',
              padding: '5px 12px', background: '#f6f6f7', borderBottom: '1px solid #e1e3e5',
            }}>
              {(['Product', 'SKU', 'Qty', 'Price'] as const).map((h, i) => (
                <span key={h} style={{
                  fontSize: 11, fontWeight: 600, color: '#6d7175',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  textAlign: i >= 2 ? 'center' : 'left',
                }}>{h}</span>
              ))}
            </div>

            {/* Rows */}
            <div style={{
              background: '#fff',
              maxHeight: lineItems.length > MAX_VISIBLE ? MAX_VISIBLE * 42 + 'px' : 'none',
              overflowY: lineItems.length > MAX_VISIBLE ? 'auto' : 'visible',
            }}>
              {lineItems.map((li, i) => {
                const qty = itemsEditMode ? (draftQty[li.id] ?? li.quantity) : li.quantity
                const removed = itemsEditMode && qty === 0
                return (
                  <div key={li.id} style={{
                    display: 'grid', gridTemplateColumns: itemsEditMode ? '1fr 110px 96px 88px' : '1fr 110px 44px 88px',
                    padding: '8px 12px', alignItems: 'center',
                    borderTop: i > 0 ? '1px solid #f1f2f3' : 'none',
                    background: removed ? '#fff8f8' : '#fff',
                    transition: 'background 0.15s',
                  }}>
                    <span style={{
                      fontSize: 13, color: removed ? '#d82c0d' : '#202223',
                      textDecoration: removed ? 'line-through' : 'none',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8,
                    }}>{li.title}</span>
                    <span style={{ fontSize: 12, color: '#6d7175', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {li.sku || '—'}
                    </span>
                    {itemsEditMode ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <button
                          onClick={() => setDraftQty(d => ({ ...d, [li.id]: Math.max(0, (d[li.id] ?? li.quantity) - 1) }))}
                          style={{
                            width: 22, height: 22, border: '1px solid #c9cccf', borderRadius: 4,
                            background: '#f6f6f7', cursor: 'pointer', fontSize: 14, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >−</button>
                        <span style={{ fontSize: 13, fontWeight: 600, minWidth: 18, textAlign: 'center', color: removed ? '#d82c0d' : '#202223' }}>
                          {qty}
                        </span>
                        <button
                          onClick={() => setDraftQty(d => ({ ...d, [li.id]: (d[li.id] ?? li.quantity) + 1 }))}
                          style={{
                            width: 22, height: 22, border: '1px solid #c9cccf', borderRadius: 4,
                            background: '#f6f6f7', cursor: 'pointer', fontSize: 14, lineHeight: 1,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >+</button>
                      </div>
                    ) : (
                      <span style={{ fontSize: 13, color: '#202223', textAlign: 'center', fontWeight: 500 }}>{qty}</span>
                    )}
                    <span style={{ fontSize: 13, color: removed ? '#d82c0d' : '#202223', textAlign: 'right', textDecoration: removed ? 'line-through' : 'none' }}>
                      {li.price} EGP
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {lineItems.length > MAX_VISIBLE && !itemsEditMode && (
            <div style={{ marginTop: 3, fontSize: 12, color: '#8c9196', textAlign: 'center' }}>
              ↕ {lineItems.length} items — scroll to view all
            </div>
          )}

          {/* Edit mode footer */}
          {itemsEditMode && (
            <div style={{ marginTop: 8 }}>
              {itemsError && <div style={{ fontSize: 12, color: '#d82c0d', marginBottom: 6 }}>{itemsError}</div>}
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  disabled={savingItems}
                  onClick={async () => {
                    const changes = lineItems
                      .filter(li => (draftQty[li.id] ?? li.quantity) !== li.quantity)
                      .map(li => ({ lineItemId: li.id, quantity: draftQty[li.id] ?? li.quantity }))
                    if (changes.length === 0) { setItemsEditMode(false); return }
                    setSavingItems(true); setItemsError(null)
                    try {
                      await engineApi.editOrder(shop, definition.id, entry.id, { shopifyOrderId: entry.orderRef.shopifyOrderId, changes })
                      const r = await engineApi.orderRecord(shop, entry.orderRef.shopifyOrderId)
                      setLineItems(r.lineItems)
                      setSummary(r.summary ?? null)
                      setItemsEditMode(false)
                    } catch (e) {
                      setItemsError(e instanceof Error ? e.message : 'Failed to save changes')
                    } finally {
                      setSavingItems(false)
                    }
                  }}
                  style={{
                    background: '#008060', color: '#fff', border: 'none',
                    borderRadius: 4, padding: '5px 14px', fontSize: 12,
                    cursor: savingItems ? 'not-allowed' : 'pointer', opacity: savingItems ? 0.7 : 1,
                  }}
                >{savingItems ? 'Saving…' : 'Save Changes'}</button>
                <button
                  disabled={savingItems}
                  onClick={() => { setItemsEditMode(false); setItemsError(null) }}
                  style={{
                    background: 'none', border: '1px solid #c9cccf',
                    borderRadius: 4, padding: '5px 14px', fontSize: 12, cursor: 'pointer',
                  }}
                >Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <Divider />
      </div>

      {/* ── Row 3: Action controls ── */}
      {canWork && !isTerminal && sessionId && (
        <div style={{ marginTop: 16 }}>
          <BlockStack gap="200">
            <Select
              label="Outcome"
              options={outcomeOptions}
              value={outcomeId}
              onChange={setOutcomeId}
            />
            <TextField
              label="Note (optional)"
              value={note}
              onChange={setNote}
              multiline={2}
              autoComplete="off"
            />
            <Button
              variant="primary"
              loading={closingSession}
              onClick={handleCloseSession}
              disabled={!outcomeId}
            >
              Submit & Close Session
            </Button>
            {actionError && (
              <Text as="p" variant="bodySm" tone="critical">{actionError}</Text>
            )}
          </BlockStack>

          {/* Escalation — only when session is open + workflow supports it */}
          {canEscalate && definition.capabilities.escalationOrigin && (
            <EscalationSection
              shop={shop}
              surface={surface}
              entry={entry}
              onEscalated={onMutated}
            />
          )}

          {/* Order creation (new / exchange / return) is now done via the Order
              Requests ticket flow (CS prepares → Operations approves) for Jessica.
              Hidden here, not removed. See docs/jessica-fulfillment-flow.md §10. */}
          {false && (canOrderCreate || canOrderExchange || canOrderReturn) && (
            <OrderCreationSection
              shop={shop}
              workflowId={definition.id}
              entryId={entry.id}
              shopifyOrderId={entry.orderRef.shopifyOrderId}
              customer={customer}
              defaultLineItems={lineItems}
              canCreate={canOrderCreate}
              canExchange={canOrderExchange}
              canReturn={canOrderReturn}
            />
          )}
        </div>
      )}

      {/* Loading / error before session is ready */}
      {canWork && !isTerminal && !sessionId && !recLoading && (
        <div style={{ marginTop: 16 }}>
          {sessionError
            ? (
              <InlineStack gap="200" blockAlign="center">
                <Text as="p" variant="bodySm" tone="critical">Failed to open session.</Text>
                <Button
                  size="slim" variant="plain"
                  onClick={() => {
                    setSessionError(false)
                    engineApi.openSession(shop, definition.id, entry.id)
                      .then(({ session }) => setSessionId(session.id))
                      .catch(() => setSessionError(true))
                  }}
                >
                  Retry
                </Button>
              </InlineStack>
            )
            : <Text as="p" variant="bodySm" tone="subdued">Opening session…</Text>
          }
        </div>
      )}

      {/* ── Row 4: Order History + Delivery Timeline + Customer History ── */}
      <div style={{ marginTop: 16, borderTop: '1px solid #e1e3e5', paddingTop: 12 }}>
        <TimelineSection
          shop={shop}
          shopifyOrderId={entry.orderRef.shopifyOrderId}
          lastStatus={lastStatusLabel}
          lastDate={lastDate}
        />
        {record?.['tracking_number'] && (
          <DeliveryTimelineSection
            shop={shop}
            trackingNumber={record['tracking_number']}
          />
        )}
        {health && health.recentOrders.length > 0 && (
          <CustomerHistorySection
            orders={health.recentOrders}
            currentOrderName={entry.orderRef.orderName}
          />
        )}
      </div>

    </div>
  )
}
